import React, { useEffect, useRef, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  Tooltip,
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Phone } from "lucide-react";
import { useParams } from "react-router-dom";
import { vURL } from "../../Url";

// 🔥 Fix default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
});

// 🔥 Fit Bounds
function FitBounds({ locations }) {
  const map = useMap();

  useEffect(() => {
    if (!locations?.length) return;

    const valid = locations.filter((loc) => loc?.latitude && loc?.longitude);

    if (!valid.length) return;

    if (valid.length === 1) {
      map.setView([valid[0].latitude, valid[0].longitude], 12);
      return;
    }

    const bounds = valid.map((loc) => [loc.latitude, loc.longitude]);

    map.fitBounds(bounds, { padding: [50, 50] });
  }, [locations, map]);

  return null;
}

// 🔥 Custom Marker with Label
const getCustomIcon = (color, name) =>
  L.divIcon({
    className: "",
    html: `
      <div style="display:flex; flex-direction:column; align-items:center; transform:translateY(-10px)">
        
        <div style="
          background:white;
          padding:4px 8px;
          border-radius:8px;
          font-size:11px;
          font-weight:500;
          white-space:nowrap;
          box-shadow:0 2px 6px rgba(0,0,0,0.15);
          margin-bottom:4px;
        ">
          ${name || "Location"}
        </div>

        <div style="
          width:22px;
          height:22px;
          background:${color};
          border-radius:50% 50% 50% 0;
          transform:rotate(-45deg);
          position:relative;
        ">
          <div style="
            width:10px;
            height:10px;
            background:white;
            border-radius:50%;
            position:absolute;
            top:6px;
            left:6px;
          "></div>
        </div>

      </div>
    `,
    iconSize: [120, 40],
    iconAnchor: [60, 30],
  });

const moduleLabels = {
  organizations: "Organization",
  channel_partners: "VS Center",
  sevaks: "Sevak",
};

  const moduleColors = {
    organizations: "#ef4444",
    channel_partners: "#0056a5",
    sevaks: "#16a34a",
  }

const moduleAPI = {
  organizations: "billing-addresses",
  channel_partners: "channel_partners-location",
  sevaks: "sevak-location",
};

function MapMobile({ country_name, state_name, city_name, search }) {
  const { module } = useParams();

  const markerRefs = useRef({});
  const [activeIndex, setActiveIndex] = useState(null);
;

  const { data: map_data, isPending } = useQuery({
    queryKey: ["map_data", country_name, state_name, city_name, module, search],
    queryFn: async () => {
      const res = await axios.get(
        `${vURL}/api/v1/public/${moduleAPI[module]}`,
        {
          params: {
            search: search,
            country_name,
            state_name,
            city_name,
          },
        },
      );
      return res.data;
    },
    staleTime: 1000 * 60 * 60,
  });

  const locations = map_data?.data || [];

  return (
    <>
      <div className="flex items-center justify-between border-b border-orange-100 bg-gradient-to-r from-orange-50/70 via-white to-white px-5 py-4">
        {/* Left section */}
        <div>
          <div className="text-[14px] font-bold text-orange-950 tracking-wide">
            Map View
          </div>

          <div className="text-[12px] text-gray-500 mt-0.5">
            Filtered location overview
          </div>
        </div>

        {/* Right badge */}
        <div className="flex items-center gap-3">
          {isPending ? (
            <div className="h-8 w-[150px] rounded-xl bg-orange-100 animate-pulse" />
          ) : (
            <div className="flex items-center gap-2 rounded-2xl border border-orange-200 bg-white px-3 py-2 shadow-sm">
              {/* Count */}
              <div className="flex h-8 min-w-8 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 px-2 text-[14px] font-bold text-white shadow-sm">
                {locations.length || 0}
              </div>

              {/* Text */}
              <div className="flex flex-col leading-tight">
                <span className="text-[14px] font-semibold text-gray-800">
                  {moduleLabels[module] || "Location"}
                  {locations.length !== 1 ? "s" : ""}
                </span>

                <span className="text-[12px] text-gray-600">found on map</span>
              </div>
            </div>
          )}
        </div>
      </div>
      <MapContainer
        center={[20.5937, 78.9629]}
        zoom={5}
        style={{ height: "600px", width: "100%" }}
        closePopupOnClick={true}
      >
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <FitBounds locations={locations} />

        {/* 🔥 CLUSTER GROUP */}
        <MarkerClusterGroup chunkedLoading>
          {locations.map((loc, i) => {
            if (!loc?.latitude || !loc?.longitude) return null;

            return (
              <Marker
                key={i}
                position={[Number(loc.latitude), Number(loc.longitude)]}
                icon={getCustomIcon(
                  moduleColors[module] || "#6b7280",
                  loc?.name,
                )}
                ref={(ref) => {
                  if (ref) markerRefs.current[i] = ref;
                }}
                eventHandlers={{
                  click: () => {
                    setActiveIndex(i);

                    setTimeout(() => {
                      markerRefs.current[i]?.openPopup();
                    }, 0);
                  },
                }}
              >
                {activeIndex === i && (
                  <>
                    <Tooltip
                      direction="top"
                      offset={[0, -15]}
                      permanent
                      className=" !capitalize"
                    >
                      {loc?.name}
                    </Tooltip>
                    <Popup
                      closeButton={false}
                      offset={[0, -12]}
                      className="custom-popup !p-0"
                    >
                      <div className="w-[260px] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                        {/* IMAGE */}
                        <div className="relative h-[120px] w-full overflow-hidden">
                          {loc?.logo || loc?.picture ? (
                            <img
                              src={loc.logo || loc?.picture}
                              alt={loc?.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-orange-100">
                              <span className="text-2xl font-bold text-orange-600 uppercase">
                                {loc?.name?.charAt(0) || "L"}
                              </span>
                            </div>
                          )}

                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

                          <h4 className="absolute bottom-2 left-3 text-white text-[14px] font-semibold !capitalize">
                            {loc?.name || "-"}
                          </h4>
                        </div>

                        {/* CONTENT */}
                        <div className="p-3 space-y-3">
                          {loc?.address && (
                            <div className="flex items-center capitalize gap-2 text-[13px] text-gray-600">
                              <MapPin
                                size={18}
                                className="mt-[3px] text-[#0056a5] shrink-0"
                              />
                              <span>{loc.address}</span>
                            </div>
                          )}

                          {(loc?.phone || loc?.telephone) && (
                            <div className="flex items-center gap-2 text-[13px] text-gray-700">
                              <Phone
                                size={18}
                                className="text-[#0056a5] shrink-0"
                              />
                              <span className="font-medium">
                                {loc.phone || loc?.telephone}
                              </span>
                            </div>
                          )}

                          <div className="border-t border-gray-100" />

                          <a
                            href={loc?.subdomain}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full block text-center px-3 py-2 bg-gradient-to-r from-[#0056a5] to-[#007bff] !text-white text-[13px] font-medium rounded-lg"
                          >
                            Open Link →
                          </a>
                        </div>
                      </div>
                    </Popup>
                  </>
                )}
              </Marker>
            );
          })}
        </MarkerClusterGroup>
      </MapContainer>

      {/* 🔥 Remove popup padding */}
      <style>{`
        .custom-popup .leaflet-popup-content {
          margin: 0 !important;
          padding: 0 !important;
        }
        .custom-popup .leaflet-popup-content-wrapper {
          padding: 0 !important;
          border-radius: 16px;
          border: none !important;
        }
        .custom-popup .leaflet-popup-tip {
          display: none;
        }
      `}</style>
    </>
  );
}

export default MapMobile;
